import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { UiIllustrationComponent } from '../illustration/ui-illustration.component';

/**
 * refresh-parent-portal-ui 0.1: a soft-background callout. Kind info (sky)
 * or warn (peach); optional leading illustration name replaces the icon.
 */
@Component({
  selector: 'ui-callout',
  standalone: true,
  imports: [MatIconModule, UiIllustrationComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="callout">
      @if (illustration()) {
        <ui-illustration [name]="illustration()" [size]="22" />
      } @else if (iconOrDefault()) {
        <span class="circle"><mat-icon>{{ iconOrDefault() }}</mat-icon></span>
      }
      <div class="text"><ng-content></ng-content></div>
    </div>
  `,
  styles: [
    `
      .callout {
        display: flex;
        gap: 10px;
        /* Owner (round 2): the icon and the text are always vertically centred. */
        align-items: center;
        border-radius: var(--gp-radius-row);
        padding: 12px;
        font-size: 14px;
        font-weight: 600;
        line-height: 19px;
        margin-top: 12px;
      }
      .circle {
        background: #fff;
        border-radius: 50%;
        padding: 6px;
        color: var(--gp-info);
        display: grid;
        place-items: center;
      }
      mat-icon {
        font-family: 'Material Symbols Rounded', 'Material Icons';
      }
      :host([data-kind='info']) .callout {
        background: var(--gp-info-bg);
        color: var(--gp-info);
        border: 1.5px solid var(--gp-tile-sky-border);
      }
      :host([data-kind='pending']) .callout {
        background: var(--gp-pending-bg);
        color: #6b4d07;
        border: 1.5px solid var(--gp-tile-butter-border);
      }
      :host([data-kind='warn']) .callout {
        background: #fff4e5;
        color: #7a4a0b;
        border: 1.5px solid #f3d5a8;
      }
      :host([data-kind='success']) .callout {
        background: var(--gp-success-bg);
        color: var(--gp-success);
        border: 1.5px solid var(--gp-tile-sage-border);
      }
      :host([data-kind='neutral']) .callout {
        background: var(--gp-neutral-bg);
        color: var(--gp-ink);
        border: 1.5px solid var(--gp-border);
      }
      /* Owner (round 2): light red — a message that lists what blocks the save. */
      :host([data-kind='danger']) .callout {
        background: var(--gp-danger-bg);
        color: var(--gp-danger);
        border: 1.5px solid #f3c4be;
      }
    `,
  ],
  host: {
    '[attr.data-kind]': 'kind()',
  },
})
export class UiCalloutComponent {
  readonly kind = input<'info' | 'warn' | 'pending' | 'success' | 'neutral' | 'danger'>('info');
  readonly illustration = input('');
  readonly icon = input('auto_awesome');

  static readonly KIND_ICONS: Record<string, string> = {
    info: 'auto_awesome',
    warn: 'error',
    pending: 'radio_button_unchecked',
    success: 'check_circle',
    danger: 'error',
    neutral: 'more_horiz',
  };

  readonly iconOrDefault = computed(() => {
    const i = this.icon();
    if (i) return i;
    return UiCalloutComponent.KIND_ICONS[this.kind()] ?? '';
  });
}